import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Modal } from "../common/ui/modal";
import Button from "../common/ui/button/Button";
import { apiClient } from "../../utils/apiClient";

type FeedbackModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [useCase, setUseCase] = useState("");
  const [painPoint, setPainPoint] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!useCase.trim() || !painPoint.trim()) {
      setError("Please answer both questions");
      setLoading(false);
      return;
    }

    try {
      await apiClient.submitFeedback({
        useCase: useCase.trim(),
        painPoint: painPoint.trim(),
      });
      setSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to submit feedback";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setUseCase("");
      setPainPoint("");
      setError(null);
      setSuccess(false);
    }, 200);
  };

  return createPortal(
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Help shape Casekeep"
      maxWidth="480px"
    >
      {success ? (
        <div className="text-center py-8">
          <div className="text-green-600 dark:text-green-400 text-xl mb-2">
            Thank you for your feedback!
          </div>
          <p className="text-gray-500 dark:text-gray-400">
            Your input helps us build a better product.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label
              htmlFor="useCase"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              1. What feature are your or do you intend to use most in Casekeep
              (and why)?
            </label>
            <textarea
              id="useCase"
              value={useCase}
              onChange={(e) => setUseCase(e.target.value)}
              placeholder="e.g., Collecting client documents, sending contracts for signature..."
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-brand-500 focus:border-transparent min-h-[80px] resize-none"
              rows={3}
            />
          </div>

          <div>
            <label
              htmlFor="painPoint"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              2. What feature(s) is Casekeep missing that would improve your
              workflows?
            </label>
            <textarea
              id="painPoint"
              value={painPoint}
              onChange={(e) => setPainPoint(e.target.value)}
              placeholder="e.g., What things do you think could improve?"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-brand-500 focus:border-transparent min-h-[80px] resize-none"
              rows={3}
            />
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-800 dark:text-red-200">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="md"
              disabled={loading}
            >
              {loading ? "Sending..." : "Submit feedback"}
            </Button>
          </div>
        </form>
      )}
    </Modal>,
    document.body,
  );
};
